import { ImgID } from "../../../image_loader/img_id.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import {
  MyTextAlign,
  MyTextVerticalAlign,
  UITextElement,
} from "../../common_ui_elements/ui_text_element.js";

/** `prize_shot` — the Prize Shot round: its logo and balloons instead of Royal Smash's. */
export type LoadingGameVariant = 'default' | 'prize_shot';

export class LoadingGameScreen extends UIElement
{
  private static readonly BACKGROUND_COLOR = "#0039B9";
  private static readonly GLOW_COLOR = "0, 120, 253";
  private static readonly ART_SCALE = 1.1;
  private static readonly BASE_ASPECT_RATIO = 1080 / 1920;
  private static readonly TALL_ASPECT_RATIO = 0.4;
  private static readonly WIDE_ASPECT_RATIO = 0.75;
  private static readonly MAX_TALL_ART_Y_OFFSET = 180;
  private static readonly MAX_WIDE_ART_Y_OFFSET = 110;
  private static readonly MAX_WIDE_ART_X_OFFSET = 75;
  private static readonly LOADING_TEXT_Y = 1505;
  // Prize Shot mockup (1080x1920): same blue, no extra glow (the balloons carry their own), text higher.
  private static readonly PRIZE_SHOT_LOADING_TEXT_Y = 1145;

  // The artwork never animates, so one paint is enough — see update(). The art
  // is only held to tell whether that paint had every bitmap in hand.
  private art: UIImage[] = [];
  private art_settled = false;

  private glow_gradient: CanvasGradient | null = null;
  private glow_gradient_key = "";

  private readonly art_scale: number;

  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, private readonly variant: LoadingGameVariant = 'default' )
  {
    super( ctx, x, y, width, height, false, false );

    this.art_scale = variant === 'prize_shot' ? 1 : LoadingGameScreen.ART_SCALE;
    if ( variant === 'prize_shot' )
      this.createPrizeShotArt();
    else
    {
      this.createLogo();
      this.createCannonComposition();
    }
    this.createLoadingText();
  }

  private createPrizeShotArt(): void
  {
    const content_x = this.getDesignContentX();
    this.addImage( ImgID.UI_PrizeShotLogo, content_x + 322, 418, 435, 279 );
    this.addImage( ImgID.UI_PrizeShotLoadingArt, content_x + 273, 670, 506, 513 );
  }

  private createLogo(): void
  {
    const content_x = this.getDesignContentX();

    this.addImage( ImgID.UI_LoadingLogoRoyal, content_x + 368.21, 444, 386, 158 );
    this.addImage( ImgID.UI_LoadingLogoSmash, content_x + 335.21, 586, 451, 126 );
    this.addImage( ImgID.UI_LoadingLogoCrown, content_x + 297.79, 346.5, 160.77, 157.78, -10 );
  }

  private createCannonComposition(): void
  {
    const content_x = this.getDesignContentX();
    this.addImage( ImgID.UI_LoadingCannonWithBarrel, content_x + 162, 757, 712, 523 );
  }

  private createLoadingText(): void
  {
    const scale = this.art_scale;
    const text_w = LayoutScaler.widthFromDesign( 650 * scale );
    const text_h = LayoutScaler.heightFromDesign( 100 * scale );
    // Under Prize Shot's balloons the text moves with the art; the cannon's sits at a fixed height.
    const text_y = this.variant === 'prize_shot'
      ? LoadingGameScreen.PRIZE_SHOT_LOADING_TEXT_Y + this.getArtworkYOffset()
      : LoadingGameScreen.LOADING_TEXT_Y;

    const loading_text = new UITextElement(
      this.ctx,
      this.rect.getW2() - text_w / 2,
      LayoutScaler.yFromDesign(
        text_y - ( 100 * scale - 100 ) / 2
      ),
      text_w,
      text_h,
      {
        localization_key: "LOADING",
        font_size: LayoutScaler.textSizeFromDesign( 48 * scale ),
        font_weight: 900,
        text_color: "#FFFFFF",
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
      }
    );
    loading_text.setClickable( false );
    this.addChild( loading_text );
  }

  private addImage(
    image_id: ImgID,
    design_x: number,
    design_y: number,
    design_w: number,
    design_h: number,
    rotation_deg = 0
  ): void
  {
    const scaled_w = design_w * this.art_scale;
    const scaled_h = design_h * this.art_scale;
    const scaled_x = design_x
      - ( scaled_w - design_w ) / 2
      + this.getWideArtworkXOffset();
    const scaled_y = design_y
      - ( scaled_h - design_h ) / 2
      + this.getArtworkYOffset();

    const image = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( scaled_x ),
      LayoutScaler.yFromDesign( scaled_y ),
      LayoutScaler.widthFromDesign( scaled_w ),
      LayoutScaler.heightFromDesign( scaled_h ),
      image_id
    );
    image.setClickable( false );
    if ( rotation_deg !== 0 )
    {
      image.enableRotation();
      image.setRotationImmediate( rotation_deg * Math.PI / 180 );
    }
    this.addChild( image );
    this.art.push( image );
  }

  private getDesignContentX(): number
  {
    return ( this.rect.getW() - LayoutScaler.widthFromDesign( 1080 ) )
      / ( 2 * ( LayoutScaler.widthFromDesign( 1 ) || 1 ) );
  }

  private getArtworkYOffset(): number
  {
    return LoadingGameScreen.MAX_WIDE_ART_Y_OFFSET * this.getWideScreenFactor()
      + LoadingGameScreen.MAX_TALL_ART_Y_OFFSET * this.getTallScreenFactor();
  }

  private getWideArtworkXOffset(): number
  {
    return LoadingGameScreen.MAX_WIDE_ART_X_OFFSET * this.getWideScreenFactor();
  }

  private getWideScreenFactor(): number
  {
    const aspect_ratio = this.rect.getW() / Math.max( 1, this.rect.getH() );
    const range = LoadingGameScreen.WIDE_ASPECT_RATIO - LoadingGameScreen.BASE_ASPECT_RATIO;
    return Math.max( 0, Math.min(
      1,
      ( aspect_ratio - LoadingGameScreen.BASE_ASPECT_RATIO ) / range
    ) );
  }

  private getTallScreenFactor(): number
  {
    const aspect_ratio = this.rect.getW() / Math.max( 1, this.rect.getH() );
    const range = LoadingGameScreen.BASE_ASPECT_RATIO - LoadingGameScreen.TALL_ASPECT_RATIO;
    return Math.max( 0, Math.min(
      1,
      ( LoadingGameScreen.BASE_ASPECT_RATIO - aspect_ratio ) / range
    ) );
  }

  private drawBackgroundAndGlow(): void
  {
    const w = this.rect.getW();
    const h = this.rect.getH();

    this.ctx.fillStyle = LoadingGameScreen.BACKGROUND_COLOR;
    this.ctx.fillRect( -w / 2, -h / 2, w, h );
    if ( this.variant === 'prize_shot' )
      return;

    // XD: X15 Y408 W1050 H1050 on the 1080x1920 artboard.
    const glow_center_y = LayoutScaler.yFromDesign(
      933 + this.getArtworkYOffset()
    ) - h / 2;
    const glow_center_x = LayoutScaler.xFromDesign( this.getWideArtworkXOffset() );
    const glow_radius = Math.min(
      LayoutScaler.widthFromDesign( 525 * LoadingGameScreen.ART_SCALE ),
      LayoutScaler.heightFromDesign( 525 * LoadingGameScreen.ART_SCALE )
    );
    const gradient_key = `${glow_center_x}|${glow_center_y}|${glow_radius}`;
    if ( !this.glow_gradient || this.glow_gradient_key !== gradient_key )
    {
      const gradient = this.ctx.createRadialGradient(
        glow_center_x,
        glow_center_y,
        0,
        glow_center_x,
        glow_center_y,
        glow_radius
      );
      gradient.addColorStop( 0, `rgba(${LoadingGameScreen.GLOW_COLOR}, 0.9)` );
      gradient.addColorStop( 0.55, `rgba(${LoadingGameScreen.GLOW_COLOR}, 0.55)` );
      gradient.addColorStop( 1, `rgba(${LoadingGameScreen.GLOW_COLOR}, 0)` );

      this.glow_gradient = gradient;
      this.glow_gradient_key = gradient_key;
    }

    this.ctx.fillStyle = this.glow_gradient;
    this.ctx.beginPath();
    this.ctx.arc( glow_center_x, glow_center_y, glow_radius, 0, Math.PI * 2 );
    this.ctx.fill();
  }

  // Kept for compatibility with loading-screen callers. This design has no
  // visible progress bar, so there is nothing to redraw for a progress change.
  public setProgress( _progress: number ): void
  {
  }

  public update( now: number ): void
  {
    this.updateBasic( now );
    if ( this.art_settled )
      return;

    this.invalidateSubtree();
    this.art_settled = this.art.every( image => ( image.getImage()?.width ?? 0 ) > 1 );
  }

  public name(): string
  {
    return "LoadingGameScreen";
  }

  protected override syncDomContent( node: HTMLElement ): boolean
  {
    if ( this.variant === 'prize_shot' )
    {
      // The browser normalizes the color it reads back, so compare against what was set.
      if ( node.dataset.bg !== 'plain' )
      {
        node.dataset.bg = 'plain';
        node.style.background = LoadingGameScreen.BACKGROUND_COLOR;
      }
      return true;
    }
    const w = this.rect.getW();
    const glow_center_y = LayoutScaler.yFromDesign( 933 + this.getArtworkYOffset() );
    const glow_center_x = LayoutScaler.xFromDesign( this.getWideArtworkXOffset() ) + w / 2;
    const glow_radius = Math.min(
      LayoutScaler.widthFromDesign( 525 * LoadingGameScreen.ART_SCALE ),
      LayoutScaler.heightFromDesign( 525 * LoadingGameScreen.ART_SCALE )
    );
    const c = LoadingGameScreen.GLOW_COLOR;
    const background = `radial-gradient(circle ${glow_radius}px at ${glow_center_x}px ${glow_center_y}px, rgba(${c}, 0.9) 0%, rgba(${c}, 0.55) 55%, rgba(${c}, 0) 100%) no-repeat, ${LoadingGameScreen.BACKGROUND_COLOR}`;
    if ( node.style.background !== background )
      node.style.background = background;
    return true;
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;

    this.startRender();
    if ( !this.dom_mode )
      this.drawBackgroundAndGlow();
    this.finishRender();
  }
}
